import React, { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { TailSpin } from "react-loader-spinner";
import "./dashboardV2.css";

const TotalOtpsCountGraph = ({ apiEndpoint, title }) => {
  const today = new Date();
  const initialEndDate = new Date(today.setHours(23, 59, 59, 999));
  const initialStartDate = new Date(today.setDate(today.getDate() - 6));

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");

  const fetchData = async () => {
    setLoading(true);
    const response = await axios.post(apiEndpoint, {
      start_date: startDate.toISOString().split("T")[0],
      end_date: endDate.toISOString().split("T")[0],
    });
    setChartData(formatDataForChart(response.data, title));
    setLoading(false);
  };

  const formatDataForChart = (data) => {
    return Object.keys(data.opt_count).map((key) => ({
      date: key,
      count: data.opt_count[key],
    }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="dashboard-v2-tooltip">
        <div className="dashboard-v2-tooltip-label">{label}</div>
        <div className="dashboard-v2-tooltip-value">{payload[0].value?.toLocaleString()} OTPs</div>
      </div>
    );
  };

  return (
    <div className="dashboard-v2-chart-card">
      <div className="dashboard-v2-chart-header">
        <h2 className="dashboard-v2-chart-title">{title}</h2>
        <div className="dashboard-v2-chart-controls">
          <select
            className="dashboard-v2-select"
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <option value="line">Line Chart</option>
            <option value="bar">Bar Chart</option>
          </select>
        </div>
      </div>
      <div className="dashboard-v2-date-row">
        <DatePicker
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          className="dashboard-v2-datepicker"
          dateFormat="MM/dd/yyyy"
        />
        <DatePicker
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          className="dashboard-v2-datepicker"
          dateFormat="MM/dd/yyyy"
        />
        <button className="dashboard-v2-submit-btn" onClick={fetchData}>
          Apply
        </button>
      </div>
      {loading ? (
        <div className="dashboard-v2-loading">
          <TailSpin height={48} width={48} color="#38bdf8" ariaLabel="loading" />
        </div>
      ) : (
        chartData.length > 0 && (
          <div className="dashboard-v2-chart-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              {chartType === "line" ? (
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="lineGradientTotal" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={1} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                  <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#38bdf8", strokeWidth: 1, strokeDasharray: "4 4" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} formatter={() => "Total OTPs"} />
                  <Line type="monotone" dataKey="count" stroke="url(#lineGradientTotal)" strokeWidth={2.5} dot={{ fill: "#38bdf8", strokeWidth: 0 }} activeDot={{ r: 5, fill: "#38bdf8", stroke: "#fff", strokeWidth: 2 }} />
                </LineChart>
              ) : (
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barRadius={[6, 6, 0, 0]}>
                  <defs>
                    <linearGradient id="barGradientTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#38bdf8" stopOpacity={1} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0.9} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(56, 189, 248, 0.08)" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} formatter={() => "Total OTPs"} />
                  <Bar dataKey="count" fill="url(#barGradientTotal)" name="Total OTPs" radius={[6, 6, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )
      )}
    </div>
  );
};

export default TotalOtpsCountGraph;
